import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GlobalContext from "../../../framework/GlobalContext";
import classes from "./index.module.css";
import Shortcut from "../../../framework/Shortcut";
import Button from "../../../framework/Button";
import AddFrequency from "./AddFrequency";
import ChipInputSelect from "../../../framework/ChipInputSelect";
import { fetchBranches } from "../../../services/branch-service";
import Checkbox from "../../../framework/Checkbox";
import { Grid } from "@mui/material";
import TextInput from "../../../framework/TextInput";
import { addFrequencySlab } from "../../../services/frequency-slab";
import { displayToast } from "../../../framework/toast/toastUtils";
import { listBranchGroup } from "../../../services/branch-group-services";
const AddFrequencySlab = (props) => {
  const {
    onEscape = () => {},
    refreshData = () => {},
    currentRowData,
    isEdit = false,
  } = props;
  const [slabName, setSlabName] = useState("");
  const [branch, setBranch] = useState([]);
  const [branches, setBranches] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [frequencies, setFrequencies] = useState([
    {
      fromFrequency: null,
      toFrequency: null,
      minDays: null,
      maxDays: null,
    },
  ]);
  const [errorStates, setErrorStates] = useState({});
  const slabNameRef = useState(null);
  const branchGroupRef = useRef();
  const branchRef = useRef();
  const addFrequenciesButtonRef = useRef();
  const saveFrequenciesButtonRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const showDeleteIcon = useMemo(() => {
    if (frequencies?.length === 1) {
      return false;
    }
    return true;
  }, [frequencies?.length]);
  const fetchBranchData = useCallback(async () => {
    const _branch = await fetchBranches();
    const _branchGroup = await listBranchGroup();
    const mergedBranches = [
      ...(_branchGroup?.result?.rows || []),
      ...(_branch?.result?.rows || []),
    ];
    setBranches(mergedBranches);
  }, []);
  const addFrequencyRow = useCallback(() => {
    if (frequencies.length === 1) {
      if (frequencies[0].minDays > frequencies[0].maxDays) {
        displayToast({
          type: "error",
          title: "Maximum days should be greater than minimum days",
        });
        return;
      }
    }
    setFrequencies((prev) => {
      return [
        ...prev,
        {
          fromFrequency: null,
          toFrequency: null,
          minDays: null,
          maxDays: null,
        },
      ];
    });
  }, [frequencies]);
  const deleteFrequencyHandler = useCallback(
    (index) => {
      if (frequencies.length <= 1) return;
      const _frequencies = [...frequencies];
      const filteredFrequencies = _frequencies.filter((_, i) => i !== index);
      setFrequencies(filteredFrequencies);
      setTimeout(() => {
        let newIndex = index;
        if (newIndex === filteredFrequencies.length) {
          newIndex -= 1;
        }
        document
          .getElementById(`row_item_0_${newIndex}`)
          ?.focus({ preventScroll: true });
      }, 100);
    },
    [frequencies]
  );
  const validateForm = useCallback(async () => {
    if (!slabName) {
      displayToast({
        type: "error",
        title: "Please enter frequency slab name.",
      });
      setErrorStates((prev) => {
        return { ...prev, slabName: true };
      });
      slabNameRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_frequency_slab_name");
      return false;
    }
    if (branch.length === 0) {
      displayToast({
        type: "error",
        title: "Please at least select branch group or branch.",
      });
      branchGroupRef.current.focus({ preventScroll: true });
      setCurrentComponent("add_frequency_slab_branch");
      return false;
    }
    if (frequencies?.length) {
      const val = frequencies.some((frequency, index, arr) => {
        if (
          // index > 0 &&
          !frequency?.fromFrequency ||
          !frequency?.toFrequency ||
          frequency?.fromFrequency < arr[index - 1]?.toFrequency ||
          frequency?.toFrequency < frequency?.fromFrequency
        ) {
          displayToast({
            type: "error",
            title: "Please check again frequency ranges",
          });
          return true;
        }
        // if (frequency?.toFrequency < frequency?.fromFrequency) {
        //   displayToast({
        //     type: "error",
        //     title: "Please check again frequency ranges",
        //   });
        //   return true;
        // }
        if (frequency?.minDays > frequency?.maxDays) {
          displayToast({
            type: "error",
            title: "Maximum days should be greater than minimum days.",
          });
          return true;
        }
        return false;
      });
      return !val;
    }
    return true;
  }, [
    branch.length,
    branchGroupRef,
    frequencies,
    slabName,
    slabNameRef,
    setCurrentComponent,
  ]);
  const saveFrequencySlabHandler = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }
    const frequencyDetails = frequencies.map((frequency) => ({
      frequency: {
        min: frequency?.fromFrequency,
        max: frequency?.toFrequency,
      },
      days: {
        min: frequency?.minDays,
        max: frequency?.maxDays,
      },
    }));

    const syncFrequencyData = frequencies.map((frequency, index) => {
      return {
        frequency: {
          min: index === 0 ? null : frequencies[index - 1].toFrequency,
          max: index === frequencies.length - 1 ? null : frequency.toFrequency,
        },
        days: {
          min: frequency.minDays,
          max: frequency.maxDays,
        },
      };
    });

    const selectedBranchIds = branch?.reduce((acc, branch) => {
      if (branch?.branchCode) {
        acc.push(...branch.branchCode);
      } else if (branch?.code) {
        acc.push(branch.code);
      }
      return acc;
    }, []);
    const branchDetails = [...new Set(selectedBranchIds)];

    const payload = {
      programName: slabName,
      frequencyProgramDetails: frequencyDetails,
      branchCode: branchDetails,
      syncFrequencyData: syncFrequencyData,
      remarks: remarks,
    };
    await addFrequencySlab(payload).then((response) => {
      if (response?.status === "success") {
        displayToast({
          type: "success",
          title: isEdit
            ? "Frequency slab updated successfully."
            : "Frequency slab added successfully.",
        });
        onEscape();
        refreshData();
      }
    });
  }, [
    validateForm,
    frequencies,
    branch,
    slabName,
    remarks,
    isEdit,
    onEscape,
    refreshData,
  ]);
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      const ctrlKey = event.ctrlKey;
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "+") {
        addFrequenciesButtonRef?.current?.click();
        return;
      }
      if (ctrlKey && key === "s") {
        saveFrequenciesButtonRef?.current?.click();
        return;
      }
    },
    [onEscape]
  );

  const initializeState = useCallback(() => {
    if (!isEdit || !currentRowData) return;
    setSlabName(currentRowData.slabName || "");
    const branchCode = currentRowData.branchCode;
    const filteredBranches = branches.filter((b) =>
      branchCode.includes(b.code || b.branchGroupId)
    );
    setBranch(filteredBranches || []);
    setRemarks(currentRowData?.remarks || "");
    setFrequencies(currentRowData.frequencySlabDetails);
  }, [isEdit, currentRowData, branches]);

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);
  useEffect(() => {
    fetchBranchData();
  }, [fetchBranchData]);
  useEffect(() => {
    slabNameRef.current.focus({ preventScroll: true });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    initializeState();
  }, [initializeState]);

  return (
    <div id="add_frequency_slab" className={classes["add-frequency-slab"]}>
      <div className={classes["add-frequency-slab-header"]}>
        <div className={classes["add-frequency-slab-header-label"]}>
          Add Frequency Slab
        </div>
        <div className={classes["add-frequency-slab-header-fields"]}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextInput
                id="add_frequency_slab_name"
                label="Frequency Slab Name"
                ref={slabNameRef}
                value={slabName}
                onChange={(e) => {
                  setSlabName(e.target.value);
                  setErrorStates((prev) => {
                    return { ...prev, slabName: false };
                  });
                }}
                required={true}
                error={errorStates.slabName}
                onFocus={() => setCurrentComponent("add_frequency_slab_name")}
                onBlur={
                  !slabName
                    ? () => {
                        setErrorStates((prev) => {
                          return { ...prev, slabName: true };
                        });
                        displayToast({
                          type: "error",
                          title: "Please enter frequency slab name.",
                        });
                      }
                    : () => {}
                }
                inputProps={{
                  "data-markers": JSON.stringify({
                    down: "",
                    left: "add_frequency_slab_name",
                    right: "add_frequency_slab_branch",
                    up: "add_frequency_slab_name",
                  }),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ChipInputSelect
                id="add_frequency_slab_branch"
                value={branch || null}
                options={branches}
                groupBy={(option) =>
                  option?.code ? "Branches" : "Branch Group"
                }
                name="branch"
                ref={branchRef}
                getOptionLabel={(option) =>
                  `${option?.name || ""}- ${option?.code ? "BR" : "BG"}`
                }
                isOptionEqualToValue={(option, value) => {
                  return option === value;
                }}
                onFocus={() => {
                  setCurrentComponent("add_frequency_slab_branch");
                }}
                onChange={(event, option) => {
                  setBranch(option);
                }}
                onBlur={
                  branch?.length === 0
                    ? () => {
                        displayToast({
                          type: "error",
                          title: "Please atleast select branch group or branch",
                        });
                      }
                    : () => {}
                }
                openOnFocus={true}
                label="Branches"
                multiple={true}
                blurOnSelect={false}
                disableCloseOnSelect={true}
                renderGroup={(params) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        padding: "5px",
                        gap: "5px",
                      }}
                      key={params.key}
                    >
                      <div
                        style={{
                          height: "30px",
                          width: "100%",
                          background: "#e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          padding: "5px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                        }}
                      >
                        {params.group}
                      </div>
                      <div>{params.children}</div>
                    </div>
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  return (
                    <div
                      {...props}
                      style={{
                        display: "flex",
                        height: "40px",
                        width: "100%",
                        padding: "1px 5px",
                        gap: "5px",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItem: "center",
                        }}
                      >
                        <Checkbox checked={selected} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItem: "center",
                          marginRight: "5px",
                        }}
                      >
                        {option?.branchGroupId ? "BG |" : "BR |"}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItem: "center",
                          gap: "5px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "15px",
                            width: "100%",
                          }}
                        >
                          {option?.name} (
                          {option?.code || option?.branchGroupId})
                        </div>
                      </div>
                    </div>
                  );
                }}
                markers={JSON.stringify({
                  up: "add_frequency_slab_start_date",
                  down: "row_item_0_0",
                  left: "add_frequency_slab_name",
                  right: "add_frequency_slab_remarks",
                })}
              />
            </Grid>
            <Grid item xs={3}>
              <TextInput
                id="add_frequency_slab_remarks"
                label="Remarks"
                value={remarks}
                onChange={(e) => {
                  setRemarks(e.target.value);
                }}
                onFocus={() =>
                  setCurrentComponent("add_frequency_slab_remarks")
                }
                inputProps={{
                  "data-markers": JSON.stringify({
                    up: "add_frequency_slab_branch",
                    down: "row_item_0_0",
                    left: "add_frequency_slab_branch",
                    right: "row_item_0_0",
                  }),
                }}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={classes["add-frequency-subcontainer"]}>
        <div style={{ height: "auto", maxHeight: "85%", width: "100%" }}>
          <div className={classes["add-frequency-title-container"]}>
            <div className={classes["add-frequency-title"]}>
              Add Frequency Slabs
            </div>
            <Button
              label="ADD"
              color="black"
              ref={addFrequenciesButtonRef}
              onClick={addFrequencyRow}
              endIcon={<Shortcut keys={["+"]} />}
            />
          </div>
          <div className={classes["add-frequency-details-container"]}>
            <div className={classes["add-frequency-details"]}>
              {frequencies?.map((frequency, index) => {
                return (
                  <AddFrequency
                    key={`${index}_${frequency.name}`}
                    index={index}
                    frequencyRowData={frequency}
                    showDeleteIcon={showDeleteIcon}
                    frequencies={frequencies}
                    onDeleteFrequency={() => deleteFrequencyHandler(index)}
                    onChange={(key, data) => {
                      setFrequencies((prev) => {
                        prev[index][key] = data;
                        return [...prev];
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <div className={classes["add-frequency-cta-container"]}>
          <Button
            label="Save"
            ref={saveFrequenciesButtonRef}
            onClick={saveFrequencySlabHandler}
            color="violet"
            endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          />
        </div>
      </div>
    </div>
  );
};

export default AddFrequencySlab;
